/**
 * Replicação de hóspedes entre as reservas de uma cotação (Fase 2).
 *
 * Pedido do time de vendas em 2026-09-03: numa cotação com vários hotéis, o operador prossegue
 * para a reserva e precisa digitar os mesmos hóspedes N vezes. Na maioria esmagadora dos casos
 * são as mesmas pessoas.
 *
 * O que torna isto menos trivial do que parece: **cada trecho carrega a própria composição de
 * pax** (`adultos`, `criancas`, `criancasIdades` do item cotado). Duas reservas da mesma cotação
 * podem ter 2 adultos + 1 criança e 2 adultos + 2 crianças. E a validação do wizard compara a
 * idade DECLARADA da criança com a data de nascimento — replicar uma criança de 8 anos para um
 * trecho que declarou 10 reprovaria a validação, corretamente.
 *
 * Por isso a replicação é **parcial e explícita**: preenche o que encaixa, deixa em branco o que
 * não encaixa, e devolve um relatório do que ficou de fora e por quê. Nunca inventa nem força.
 *
 * Nota: a checagem de documento repetido do wizard é POR reserva, não global — a mesma pessoa
 * em quatro reservas é válida. É o que torna a replicação possível.
 */

/** Campos de identidade que viajam de uma pessoa para outra. `tipo` e `age` NUNCA são copiados. */
const copiarIdentidade = (origem, destino) => ({
	...destino,
	document: origem.document,
	nome: origem.nome,
	nascimento: origem.nascimento,
	estrangeiro: origem.estrangeiro,
	semCpf: origem.semCpf,
	dataFetched: origem.dataFetched,
	manualEdit: origem.manualEdit,
	fetching: false,
});

/** Uma pessoa "vazia" não vale a pena replicar — evita apagar dado já digitado no destino. */
const temIdentidade = (p) => !!(p && (p.nome || p.document));

const porTipo = (lista, tipo) => (lista || []).filter((p) => (p.tipo === "child" ? "child" : "adult") === tipo);

/**
 * @param {Object} hospedes  estado atual: { [trechoId]: Pessoa[] }
 * @param {Array}  reservas  [{ trecho: {id}, item: { hotel: {nome} } }] — a ordem é a da tela
 * @param {string} origemId  trechoId de onde copiar (normalmente a primeira reserva)
 * @returns {{hospedes: Object, relatorio: Array, copiadas: number}}
 *   `relatorio` só traz as reservas com alguma ressalva: { trechoId, hotel, avisos: string[] }
 *   `copiadas` = quantas reservas receberam ao menos um hóspede.
 */
export const replicarHospedes = (hospedes, reservas, origemId) => {
	const estado = { ...(hospedes || {}) };
	const relatorio = [];
	let copiadas = 0;

	const origem = (hospedes || {})[origemId] || [];
	const origAdultos = porTipo(origem, "adult").filter(temIdentidade);
	const origCriancas = porTipo(origem, "child").filter(temIdentidade);

	for (const r of reservas || []) {
		const destinoId = r?.trecho?.id;
		if (!destinoId || destinoId === origemId) continue;

		const atual = estado[destinoId] || [];
		const avisos = [];
		let recebeu = false;

		// Índices independentes por tipo: adulto puxa de adulto, criança de criança.
		let iAdulto = 0;
		let iCrianca = 0;

		const novo = atual.map((pessoa) => {
			if (pessoa.tipo === "child") {
				const fonte = origCriancas[iCrianca++];
				if (!fonte) return pessoa;
				// A idade vem da cotação e é parte do contrato da tarifa. Se difere, não dá para
				// reaproveitar a mesma criança: a validação compararia nascimento x idade e reprovaria.
				if (Number(fonte.age) !== Number(pessoa.age)) {
					avisos.push(`a criança ${iCrianca} tem ${pessoa.age} ano(s) aqui e ${fonte.age} na reserva de origem — preencha à mão`);
					return pessoa;
				}
				recebeu = true;
				return copiarIdentidade(fonte, pessoa);
			}
			const fonte = origAdultos[iAdulto++];
			if (!fonte) return pessoa;
			recebeu = true;
			return copiarIdentidade(fonte, pessoa);
		});

		const faltamAdultos = porTipo(atual, "adult").length - origAdultos.length;
		if (faltamAdultos > 0) avisos.push(`${faltamAdultos} adulto(s) a mais que a reserva de origem — preencha à mão`);
		const faltamCriancas = porTipo(atual, "child").length - origCriancas.length;
		if (faltamCriancas > 0) avisos.push(`${faltamCriancas} criança(s) a mais que a reserva de origem — preencha à mão`);

		estado[destinoId] = novo;
		if (recebeu) copiadas += 1;
		if (avisos.length) relatorio.push({ trechoId: destinoId, hotel: r?.item?.hotel?.nome || "", avisos });
	}

	return { hospedes: estado, relatorio, copiadas };
};

/** Há o que replicar? Usado para desabilitar o botão em vez de deixar o clique não fazer nada. */
export const podeReplicar = (hospedes, reservas, origemId) => {
	if (!reservas || reservas.length < 2) return false;
	return ((hospedes || {})[origemId] || []).some(temIdentidade);
};
